import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import FormattedDataPoint from './FormattedDataPoint';
import { getUserUnitI } from '../../common/ambient';

export default class Rain2Widget extends PureComponent {
  static propTypes = {
    device: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    viewDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    rangeStart: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    rangeEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    rainData: PropTypes.array // Array of {dateutc, dailyrainin} for shared posts
  };

  state = {
    viewDate: new Date(),
    rangeStart: null,
    rangeEnd: null,
    isNarrow: false
  };

  componentDidMount() {
    // If we have shared props, restore the widget state
    const { viewDate: propViewDate, rangeStart: propRangeStart, rangeEnd: propRangeEnd } = this.props;
    
    if (propViewDate || propRangeStart || propRangeEnd) {
      this.setState({
        viewDate: propViewDate ? new Date(propViewDate) : new Date(),
        rangeStart: propRangeStart ? new Date(propRangeStart) : null,
        rangeEnd: propRangeEnd ? new Date(propRangeEnd) : null
      });
    }

    // Set up ResizeObserver to monitor width - use setTimeout to ensure DOM is ready
    setTimeout(() => {
      this.setupResizeObserver();
    }, 0);
  }

  setContainerRef = (element) => {
    this.containerRef = element;
    
    // If we get a new ref and haven't set up the observer yet, do it now
    if (element && !this.resizeObserver && !this.checkWidth) {
      this.setupResizeObserver();
    }
  };

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.checkWidth) {
      window.removeEventListener('resize', this.checkWidth);
    }
  }

  setupResizeObserver = () => {
    if (!this.containerRef) {
      return;
    }

    // Check if ResizeObserver is available
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          const width = entry.contentRect.width;
          const isNarrow = width < 300;
          if (this.state.isNarrow !== isNarrow) {
            this.setState({ isNarrow });
          }
        }
      });

      this.resizeObserver.observe(this.containerRef);
      
      // Also do an initial check with current width
      const initialWidth = this.containerRef.offsetWidth;
      const initialIsNarrow = initialWidth < 300;
      if (this.state.isNarrow !== initialIsNarrow) {
        this.setState({ isNarrow: initialIsNarrow });
      }
    } else {
      // Fallback for older browsers - check on window resize
      this.checkWidth = () => {
        if (this.containerRef) {
          const width = this.containerRef.offsetWidth;
          const isNarrow = width < 300;
          if (this.state.isNarrow !== isNarrow) {
            this.setState({ isNarrow });
          }
        }
      };
      
      window.addEventListener('resize', this.checkWidth);
      // Initial check
      this.checkWidth();
    }
  };

  _getRainDataForDate = (date) => {
    const { device, rainData } = this.props;

    // Use shared rainData if available (for social posts)
    if (rainData && Array.isArray(rainData)) {
      const dateStr = moment(date).format('YYYY-MM-DD');
      const dataForDate = rainData.find(item => {
        if (!item.dateutc) return false;
        const itemDateStr = moment(item.dateutc).format('YYYY-MM-DD');
        return itemDateStr === dateStr;
      });
      
      if (dataForDate && typeof dataForDate.dailyrainin === 'number' && dataForDate.dailyrainin > 0) {
        return dataForDate.dailyrainin;
      }
      return null;
    }

    // Fall back to device summaries (normal widget usage)
    const { deviceSummaries } = device;
    if (!deviceSummaries || !Array.isArray(deviceSummaries)) {
      return null;
    }

    const dateStr = moment(date).format('YYYY-MM-DD');
    
    const summaryForDate = deviceSummaries.find(summary => {
      if (!summary.dateutc) return false;
      const summaryDateStr = moment(summary.dateutc).format('YYYY-MM-DD');
      return summaryDateStr === dateStr;
    });

    if (summaryForDate 
      && summaryForDate.dailyrainin 
      && typeof summaryForDate.dailyrainin.h === 'number'
      && summaryForDate.dailyrainin.h > 0
    ) {
      return summaryForDate.dailyrainin.h;
    }

    return null;
  };

  _getMonthlyRainTotal = () => {
    const { device, rainData } = this.props;
    const { viewDate } = this.state;

    // Use shared rainData if available (for social posts)
    if (rainData && Array.isArray(rainData)) {
      const currentMonth = moment(viewDate).format('YYYY-MM');
      let totalRain = 0;

      rainData.forEach(item => {
        if (!item.dateutc) return;
        
        const itemMonth = moment(item.dateutc).format('YYYY-MM');
        if (itemMonth === currentMonth && 
            typeof item.dailyrainin === 'number' &&
            item.dailyrainin > 0) {
          totalRain += item.dailyrainin;
        }
      });

      return totalRain;
    }

    // Fall back to device summaries (normal widget usage)
    const { deviceSummaries } = device;
    if (!deviceSummaries || !Array.isArray(deviceSummaries)) {
      return 0;
    }

    const currentMonth = moment(viewDate).format('YYYY-MM');
    let totalRain = 0;

    deviceSummaries.forEach(summary => {
      if (!summary.dateutc) return;
      
      const summaryMonth = moment(summary.dateutc).format('YYYY-MM');
      if (summaryMonth === currentMonth && 
          summary.dailyrainin && 
          typeof summary.dailyrainin.h === 'number' &&
          summary.dailyrainin.h > 0) {
        totalRain += summary.dailyrainin.h;
      }
    });

    return totalRain;
  };

  _getRainUnitSuffix = () => {
    const { user } = this.props;
    const userUnitI = getUserUnitI('dailyrainin', user);
    return userUnitI === 0 ? '"' : ''; // inches = ", mm = empty string
  };

  _handleDateChange = (date) => {
    const { rangeStart, rangeEnd } = this.state;
    
    // If no range is selected, or if range is complete, start a new range
    if (!rangeStart || (rangeStart && rangeEnd)) {
      this.setState({
        rangeStart: date,
        rangeEnd: null
      });
    } else {
      // We have a start date, now set the end date
      if (moment(date).isAfter(moment(rangeStart))) {
        // Selected date is after start date - valid range
        this.setState({
          rangeEnd: date
        });
      } else if (moment(date).isSame(moment(rangeStart), 'day')) {
        // Same date clicked - clear range
        this.setState({
          rangeStart: null,
          rangeEnd: null
        });
      } else {
        // Selected date is before start date - restart with new start
        this.setState({
          rangeStart: date,
          rangeEnd: null
        });
      }
    }
  };

  _clearRange = () => {
    this.setState({
      rangeStart: null,
      rangeEnd: null
    });
  };

  _getMonthRainValues = () => {
    const { device, rainData } = this.props;
    const { viewDate } = this.state;
    
    // Get the current month and also 6 days before/after for calendar edges
    const startOfMonth = moment(viewDate).startOf('month');
    const endOfMonth = moment(viewDate).endOf('month');
    const startRange = startOfMonth.clone().subtract(6, 'days');
    const endRange = endOfMonth.clone().add(6, 'days');
    
    let rainValues = [];

    // Use shared rainData if available
    if (rainData && Array.isArray(rainData)) {
      rainData.forEach(item => {
        if (!item.dateutc) return;
        const itemMoment = moment(item.dateutc);
        if (itemMoment.isBetween(startRange, endRange, 'day', '[]') && 
            typeof item.dailyrainin === 'number' && 
            item.dailyrainin > 0) {
          rainValues.push(item.dailyrainin);
        }
      });
    } else if (device && device.deviceSummaries) {
      // Fall back to device summaries
      device.deviceSummaries.forEach(summary => {
        if (!summary.dateutc) return;
        const summaryMoment = moment(summary.dateutc);
        if (summaryMoment.isBetween(startRange, endRange, 'day', '[]') && 
            summary.dailyrainin && 
            typeof summary.dailyrainin.h === 'number' &&
            summary.dailyrainin.h > 0) {
          rainValues.push(summary.dailyrainin.h);
        }
      });
    }

    return rainValues;
  };

  _getRainIntensityClass = (rainValue) => {
    if (!rainValue || rainValue === 0) return '';
    
    // Get all rain values for the current month
    const monthRainValues = this._getMonthRainValues();
    if (monthRainValues.length === 0) return '';
    
    // Calculate min and max
    const maxRain = Math.max(...monthRainValues);
    const minRain = Math.min(...monthRainValues);
    
    // If all values are the same, return mid-range class
    if (maxRain === minRain) return 'rain-2';
    
    // Calculate which quartile this value falls into
    const range = maxRain - minRain;
    const quarterRange = range / 4;
    
    if (rainValue <= minRain + quarterRange) {
      return 'rain-1';
    } else if (rainValue <= minRain + (quarterRange * 2)) {
      return 'rain-2';
    } else if (rainValue <= minRain + (quarterRange * 3)) {
      return 'rain-3';
    } else {
      return 'rain-4';
    }
  };

  _getDayClassName = (date) => {
    const { rangeStart, rangeEnd } = this.state;
    
    let classes = [];
    
    // Add rain intensity class
    const rainValue = this._getRainDataForDate(date);
    const rainClass = this._getRainIntensityClass(rainValue);
    if (rainClass) {
      classes.push(rainClass);
    }
    
    // Range selection classes
    if (rangeStart) {
      const dayMoment = moment(date);
      const startMoment = moment(rangeStart);
      const endMoment = rangeEnd ? moment(rangeEnd) : null;
      
      // Range start
      if (dayMoment.isSame(startMoment, 'day')) {
        classes.push('range-start');
      }
      
      // Range end
      if (endMoment && dayMoment.isSame(endMoment, 'day')) {
        classes.push('range-end');
      }
      
      // In range
      if (endMoment && dayMoment.isBetween(startMoment, endMoment, 'day', '[]')) {
        classes.push('in-range');
      }
    }
    
    return classes.join(' ');
  };

  _getRangeTotal = () => {
    const { device, rainData } = this.props;
    const { rangeStart, rangeEnd } = this.state;

    if (!rangeStart || !rangeEnd) {
      return 0;
    }

    // Use shared rainData if available (for social posts)
    if (rainData && Array.isArray(rainData)) {
      const startMoment = moment(rangeStart);
      const endMoment = moment(rangeEnd);
      let totalRain = 0;

      rainData.forEach(item => {
        if (!item.dateutc) return;
        
        const itemMoment = moment(item.dateutc);
        if (itemMoment.isBetween(startMoment, endMoment, 'day', '[]') &&
            typeof item.dailyrainin === 'number' &&
            item.dailyrainin > 0) {
          totalRain += item.dailyrainin;
        }
      });

      return totalRain;
    }

    // Fall back to device summaries (normal widget usage)
    const { deviceSummaries } = device;
    if (!deviceSummaries || !Array.isArray(deviceSummaries)) {
      return 0;
    }

    const startMoment = moment(rangeStart);
    const endMoment = moment(rangeEnd);
    let totalRain = 0;

    deviceSummaries.forEach(summary => {
      if (!summary.dateutc) return;
      
      const summaryMoment = moment(summary.dateutc);
      if (summaryMoment.isBetween(startMoment, endMoment, 'day', '[]') &&
          summary.dailyrainin && 
          typeof summary.dailyrainin.h === 'number' &&
          summary.dailyrainin.h > 0) {
        totalRain += summary.dailyrainin.h;
      }
    });

    return totalRain;
  };

  _renderCustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
    const { viewDate: propViewDate, rangeStart: propRangeStart, rangeEnd: propRangeEnd, rainData } = this.props;
    const isSharedView = propViewDate || propRangeStart || propRangeEnd || rainData;
    const monthlyTotal = this._getMonthlyRainTotal();
    const monthName = moment(date).format('MMMM YYYY');
    const rainSuffix = this._getRainUnitSuffix();
    
    return (
      <div className="custom-header">
        {!isSharedView && (
          <button
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className="nav-button prev"
          >
            {"<"}
          </button>
        )}
        <div className="month-info">
          <div className="month-name">{monthName}</div>
          <div className="month-total">
            <FormattedDataPoint 
              type="dailyrainin" 
              row={{ dailyrainin: monthlyTotal }}
              suff={rainSuffix}
            />
          </div>
        </div>
        {!isSharedView && (
          <button
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className="nav-button next"
          >
            {">"}
          </button>
        )}
      </div>
    );
  };

  _renderDayContents = (day, date) => {
    // Handle potential parameter issues in v2.3.0
    let actualDate = date;
    if (!date || !date.getFullYear) {
      // If date parameter is not properly passed, try to construct from current view
      const currentDate = this.state.viewDate || new Date();
      actualDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    }
    
    const rainValue = this._getRainDataForDate(actualDate);
    const rainSuffix = this._getRainUnitSuffix();
    
    return (
      <div className="day-content">
        <div className="day-number">{day}</div>
        {rainValue !== null && (
          <div className="rain-value">
            <FormattedDataPoint 
              type="dailyrainin" 
              row={{ dailyrainin: rainValue }}
              suff={rainSuffix}
            />
          </div>
        )}
      </div>
    );
  };

  getShareData = () => {
    const { device } = this.props;
    const { viewDate, rangeStart, rangeEnd } = this.state;
    const { deviceSummaries } = device;


    // Get minimal rainfall data for the current view
    let relevantSummaries = [];
    
    if (!deviceSummaries || !Array.isArray(deviceSummaries)) {
      console.warn('No deviceSummaries available for sharing');
      return {
        viewDate: viewDate.toISOString(),
        rangeStart: rangeStart ? rangeStart.toISOString() : null,
        rangeEnd: rangeEnd ? rangeEnd.toISOString() : null,
        rainData: []
      };
    }
    
    if (rangeStart && rangeEnd) {
      // If there's a range, get data for that range
      const startMoment = moment(rangeStart);
      const endMoment = moment(rangeEnd);
      
      relevantSummaries = deviceSummaries.filter(summary => {
        if (!summary.dateutc) return false;
        const summaryMoment = moment(summary.dateutc);
        return summaryMoment.isBetween(startMoment, endMoment, 'day', '[]');
      });
    } else {
      // Get data for the current month plus 6 days before/after for calendar edges
      const startOfMonth = moment(viewDate).startOf('month');
      const endOfMonth = moment(viewDate).endOf('month');
      const startRange = startOfMonth.clone().subtract(6, 'days');
      const endRange = endOfMonth.clone().add(6, 'days');
      
      relevantSummaries = deviceSummaries.filter(summary => {
        if (!summary.dateutc) return false;
        const summaryMoment = moment(summary.dateutc);
        return summaryMoment.isBetween(startRange, endRange, 'day', '[]');
      });
    }


    // Extract only the rainfall data we need
    const rainData = relevantSummaries.map(summary => ({
      dateutc: summary.dateutc,
      dailyrainin: summary.dailyrainin && summary.dailyrainin.h || 0
    }));

    return {
      viewDate: viewDate.toISOString(),
      rangeStart: rangeStart ? rangeStart.toISOString() : null,
      rangeEnd: rangeEnd ? rangeEnd.toISOString() : null,
      rainData
    };
  };

  render() {
    const { device, user, viewDate: propViewDate, rangeStart: propRangeStart, rangeEnd: propRangeEnd, rainData } = this.props;
    const { viewDate, rangeStart, rangeEnd, isNarrow } = this.state;


    // For shared posts, we might not have device but will have rainData
    if (!rainData && (!device || !device.deviceSummaries)) {
      return <div className="device-rain-2-widget"></div>;
    }

    const rangeTotal = this._getRangeTotal();
    const rainSuffix = this._getRainUnitSuffix();
    
    const hasRange = rangeStart && rangeEnd;
    const isSharedView = propViewDate || propRangeStart || propRangeEnd || rainData;

    const classNames = [
      'device-rain-2-widget',
      isSharedView ? 'readonly' : '',
      isNarrow ? 'narrow' : ''
    ].filter(Boolean).join(' ');

    return (
      <div ref={this.setContainerRef} className={classNames}>
        {hasRange ? (
          <div className="range-display">
            <div className="range-info">
              <div className="range-dates">
                <span className="date-range">
                  {moment(rangeStart).format('MMM D')} - {moment(rangeEnd).format('MMM D, YYYY')}
                </span>
              </div>
              <div className="range-total">
                {rangeTotal > 0 ? (
                  <FormattedDataPoint 
                    type="dailyrainin" 
                    row={{ dailyrainin: rangeTotal }}
                  />
                ) : (
                  <span className="no-data">No Rain</span>
                )}
              </div>
            </div>
            {!isSharedView && (
              <button onClick={this._clearRange} className="clear-range">
                Clear Range
              </button>
            )}
          </div>
        ) : (
          <>
            <DatePicker
              inline
              openToDate={viewDate}
              onChange={isSharedView ? () => {} : this._handleDateChange}
              onMonthChange={isSharedView ? () => {} : (date => this.setState({ viewDate: date }))}
              renderDayContents={this._renderDayContents}
              renderCustomHeader={this._renderCustomHeader}
              dayClassName={this._getDayClassName}
              readOnly={isSharedView}
              className={isSharedView ? 'readonly' : ''}
            />
            {rangeStart && !rangeEnd && !isSharedView && (
              <div className="range-helper-text">
                Select end date for range
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

Rain2Widget.displayName = 'Rain2Widget'
